import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import type { HabitFrequencyConfig } from '@/types/habits';
import { useLanguage } from '@/contexts/LanguageContext';
import { cn } from '@/lib/utils';

interface FrequencySelectorProps {
  value: HabitFrequencyConfig;
  onChange: (config: HabitFrequencyConfig) => void;
  disabled?: boolean;
}

const DAYS_OF_WEEK = [
  { value: 0, labelEn: 'Sunday', labelAr: 'الأحد', short: 'Su' },
  { value: 1, labelEn: 'Monday', labelAr: 'الإثنين', short: 'Mo' },
  { value: 2, labelEn: 'Tuesday', labelAr: 'الثلاثاء', short: 'Tu' },
  { value: 3, labelEn: 'Wednesday', labelAr: 'الأربعاء', short: 'We' },
  { value: 4, labelEn: 'Thursday', labelAr: 'الخميس', short: 'Th' },
  { value: 5, labelEn: 'Friday', labelAr: 'الجمعة', short: 'Fr' },
  { value: 6, labelEn: 'Saturday', labelAr: 'السبت', short: 'Sa' },
];

export function FrequencySelector({ value, onChange, disabled }: FrequencySelectorProps) {
  const { t, language } = useLanguage();
  const isRTL = language === 'ar';

  const handleFrequencyChange = (frequency: HabitFrequencyConfig['frequency']) => {
    onChange({
      ...value,
      frequency,
      customDays: frequency === 'custom' ? value.customDays || [1, 3, 5] : undefined,
    });
  };

  const handleTimesChange = (times: number) => {
    onChange({
      ...value,
      timesPerFrequency: Math.max(1, times),
    });
  };

  const handleDurationChange = (duration: number) => {
    onChange({
      ...value,
      duration: Math.max(1, duration),
    });
  };

  const handleCustomDayToggle = (day: number) => {
    const currentDays = value.customDays || [];
    const newDays = currentDays.includes(day)
      ? currentDays.filter(d => d !== day)
      : [...currentDays, day].sort();
    
    // Ensure at least one day is selected
    if (newDays.length > 0) {
      onChange({
        ...value,
        customDays: newDays,
      });
    }
  };

  return (
    <div className="space-y-3">
      {/* Frequency Type */}
      <div className="space-y-1.5">
        <Label className={`text-xs font-medium ${isRTL ? 'text-right' : 'text-left'}`}>
          {isRTL ? 'التكرار' : 'Frequency'}
        </Label>
        <Select
          value={value.frequency}
          onValueChange={handleFrequencyChange}
          disabled={disabled}
        >
          <SelectTrigger className={`h-9 text-sm ${isRTL ? 'text-right' : 'text-left'}`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="daily">
              {isRTL ? 'يومي' : 'Daily'}
            </SelectItem>
            <SelectItem value="weekly">
              {isRTL ? 'أسبوعي' : 'Weekly'}
            </SelectItem>
            <SelectItem value="monthly">
              {isRTL ? 'شهري' : 'Monthly'}
            </SelectItem>
            <SelectItem value="custom">
              {isRTL ? 'مخصص (أيام محددة)' : 'Custom (Specific Days)'}
            </SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Times per frequency */}
      <div className="space-y-1.5">
        <Label className={`text-xs font-medium ${isRTL ? 'text-right' : 'text-left'}`}>
          {value.frequency === 'daily' && (isRTL ? 'مرات في اليوم' : 'Times per Day')}
          {value.frequency === 'weekly' && (isRTL ? 'مرات في الأسبوع' : 'Times per Week')}
          {value.frequency === 'monthly' && (isRTL ? 'مرات في الشهر' : 'Times per Month')}
          {value.frequency === 'custom' && (isRTL ? 'مرات في اليوم المحدد' : 'Times per Selected Day')}
        </Label>
        <Input
          type="number"
          min="1"
          max="100"
          value={value.timesPerFrequency}
          onChange={(e) => handleTimesChange(parseInt(e.target.value) || 1)}
          disabled={disabled}
          className={`h-9 text-sm ${isRTL ? 'text-right' : 'text-left'}`}
        />
      </div>

      {/* Custom days selector */}
      {value.frequency === 'custom' && (
        <div className="space-y-1.5">
          <Label className={`text-xs font-medium ${isRTL ? 'text-right' : 'text-left'}`}>
            {isRTL ? 'اختر الأيام' : 'Select Days'}
          </Label>
          <div className={cn(
            "grid grid-cols-7 gap-1.5",
            isRTL && "grid-flow-dense"
          )}>
            {DAYS_OF_WEEK.map((day) => (
              <div
                key={day.value}
                className={cn(
                  "flex flex-col items-center gap-0.5 p-1.5 rounded-md border cursor-pointer transition-colors",
                  value.customDays?.includes(day.value)
                    ? "bg-primary text-primary-foreground border-primary"
                    : "hover:bg-muted",
                  disabled && "opacity-50 cursor-not-allowed"
                )}
                onClick={() => !disabled && handleCustomDayToggle(day.value)}
              >
                <Checkbox
                  checked={value.customDays?.includes(day.value)}
                  disabled={disabled}
                  className="pointer-events-none h-3 w-3"
                />
                <span className="text-[10px] font-medium">
                  {isRTL ? day.labelAr : day.short}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Duration */}
      <div className="space-y-1.5">
        <Label className={`text-xs font-medium ${isRTL ? 'text-right' : 'text-left'}`}>
          {isRTL ? 'المدة (بالأيام)' : 'Duration (Days)'}
        </Label>
        <Input
          type="number"
          min="1"
          max="365"
          value={value.duration}
          onChange={(e) => handleDurationChange(parseInt(e.target.value) || 1)}
          disabled={disabled}
          className={`h-9 text-sm ${isRTL ? 'text-right' : 'text-left'}`}
        />
        <p className="text-[10px] text-muted-foreground">
          {isRTL 
            ? `المدة الإجمالية: ${value.duration} يوم (${Math.ceil(value.duration / 7)} أسبوع تقريباً)`
            : `Total duration: ${value.duration} days (approximately ${Math.ceil(value.duration / 7)} weeks)`
          }
        </p>
      </div>
    </div>
  );
}

